import { type Dictionary } from 'lodash';
import React from 'react';
import { type IFont } from '@meitu/saas-editor-core/es/version-types';
import { TextFontFamily } from '@meitu/xdesign-widgets';
import {
    UserBizSearchProps,
    type ITextFontFamilyProps as WidgetTextFontFamilyProps,
} from '@meitu/xdesign-widgets/dist/textFontFamily';
import { useLocaleStore } from '@/i18n';
import LabelAction from '../label-action';
import { type FontItem } from './types';

interface Props extends Omit<WidgetTextFontFamilyProps<FontItem>, 'allFontData' | 'onChange' | 'usingFontList'> {
    fonts: Dictionary<FontItem[]>;
    usingFonts?: IFont[];
    enableRemoteSearch?: boolean;
    onChange: (data: FontItem, name: string, isFromSearch: boolean) => void;
}

export { type FontItem };

export const FontFamilySelector: React.FC<Props> = ({
    fonts,
    value,
    usingFonts = [],
    enableRemoteSearch = true,
    onChange,
    ...restProps
}) => {
    const { $t } = useLocaleStore();
    const label = $t((v) => v.textFontFamily.fontFamily);

    const searchProps = (() => {
        const searchEnable = restProps.searchEnable || true;
        if (!searchEnable || !enableRemoteSearch) return {};

        return {
            searchFontData: UserBizSearchProps.searchFontData as any,
            searchDebounceDelay: UserBizSearchProps.searchDebounceDelay,
        };
    })();

    const usingFontList: FontItem[] = (() => {
        if (usingFonts.length === 0) return [];

        const bizFontDict = usingFonts.reduce(
            (acc, item) => {
                const categoryName = item['category_name'] || '';
                const categoryFonts = fonts[categoryName];

                if (!Reflect.has(acc, categoryName) && categoryFonts) {
                    acc[categoryName] = categoryFonts;
                }

                return acc;
            },
            {} as Record<string, FontItem[]>,
        );

        const bizFonts = Object.keys(bizFontDict).reduce((acc, key) => {
            return acc.concat(bizFontDict[key]);
        }, [] as FontItem[]);

        return bizFonts.filter((bizFont: FontItem) =>
            usingFonts.some((editorFont: IFont) => editorFont['id'] === bizFont['id']),
        );
    })();

    const handleChange = (data: FontItem, fontName: string, isFromSearch: boolean) => {
        onChange(data, fontName, isFromSearch);
    };

    return (
        <LabelAction title={label} align>
            <TextFontFamily<FontItem>
                allFontData={fonts}
                usingFontList={usingFontList}
                value={value}
                onChange={handleChange}
                {...searchProps}
                {...restProps}
            />
        </LabelAction>
    );
};
